import './App.css';


import Layout from './Layout';

import CryptoList from './CryptoList';
import CryptoDetail from './CryptoDetail';

import FearAndGreed from './FearAndGreed';
import MacroDominanceScreen from './features/macro/MacroDominanceScreen';
import CurrencyList from './CurrencyList';
import CurrencyDetail from './CurrencyDetail';
import CryptoHeatmap from './components/CryptoHeatmap';
import BtcDominanceDetail from './BtcDominanceDetail';
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from 'react-router-dom';

// ...existing code...
import Contact from './Contact';
import UnifiedPortfolioPage from './UnifiedPortfolioPage';
import About from './About';
import React, { useState, useEffect } from 'react';
import DashboardUltraPremium from './DashboardUltraPremium';
import MarketIntelligencePage from './pages/market-intelligence/MarketIntelligencePage';
import AssetScannerPage from './pages/asset-scanner/AssetScannerPage';
import PatternScannerDetailPage from './pages/pattern-scanner/PatternScannerDetailPage';
import PatternScannerOverviewPage from './pages/pattern-scanner/PatternScannerOverviewPage';
import HomePage from './pages/home/HomePage';
import LoginPage from './pages/auth/LoginPage';
import RequireLoginRoute from './components/auth/RequireLoginRoute';
import ComingSoonPage from './pages/coming-soon/ComingSoonPage';
import { fetchPublicConfig } from './api/publicConfig';
// ...existing code...
import AdminPage from './pages/admin/AdminPage';
import ConnectionsPage from './ConnectionsPage';

function persistStoredUser(user) {
  try {
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      localStorage.removeItem('user');
    }
  } catch {
    // Ignore storage failures and keep the UI usable.
  }
}

function loadStoredUser() {
  try {
    const rawUser = localStorage.getItem('user');
    if (!rawUser) {
      return null;
    }

    const parsedUser = JSON.parse(rawUser);
    return normalizeAuthenticatedUser(parsedUser);
  } catch {
    return null;
  }
}

function normalizeAuthenticatedUser(payload) {
  if (!payload || !payload.login) {
    return null;
  }
  return {
    login: payload.login,
    name: payload.login,
    role: payload.role || 'USER',
  };
}

// Wrapper pour injecter les cryptos dans CryptoHeatmap
function CryptoHeatmapWrapper() {
  const [cryptos, setCryptos] = React.useState([]);
  React.useEffect(() => {
    fetch('/api/cryptos')
      .then(res => res.json())
      .then(data => {
        let arr = Array.isArray(data.content) ? data.content : Array.isArray(data) ? data : [];
        // On ne garde que les champs nécessaires
        arr = arr.map(c => ({
          id: c.id,
          symbol: c.symbol,
          name: c.name,
          marketCap: c.marketCap ?? c.marketcap ?? 0,
          priceChange24h: c.priceChange24h ?? 0,
          priceChangePercentage24h: c.priceChangePercentage24h ?? 0
        }));
        setCryptos(arr);
      });
  }, []);
  return <CryptoHeatmap cryptos={cryptos} />;
}


function AppRoutes() {
  // Debug log removed: [App] Composant App monté, rendu principal
  const [currencyRefresh, setCurrencyRefresh] = useState(0);
  const [publicConfig, setPublicConfig] = useState({ comingSoon: false });
  useEffect(() => {
    fetchPublicConfig().then(cfg => setPublicConfig(cfg));
  }, []);
  const [user, setUser] = useState(() => loadStoredUser());
  const [authResolved, setAuthResolved] = useState(() => Boolean(loadStoredUser()));

  useEffect(() => {
    let cancelled = false;

    const syncAuthenticatedUser = async () => {
      const fallbackUser = loadStoredUser();

      try {
        const response = await fetch('/api/users/me', { credentials: 'include' });
        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            persistStoredUser(null);
            if (!cancelled) {
              setUser(null);
            }
          } else if (!cancelled) {
            setUser(fallbackUser);
          }
          return;
        }

        const payload = await response.json();
        const nextUser = normalizeAuthenticatedUser(payload);
        persistStoredUser(nextUser);
        if (!cancelled) {
          setUser(nextUser);
        }
      } catch {
        if (!cancelled) {
          setUser(fallbackUser);
        }
      } finally {
        if (!cancelled) {
          setAuthResolved(true);
        }
      }
    };

    void syncAuthenticatedUser();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (user === undefined) {
      return;
    }
    persistStoredUser(user);
  }, [user]);

  // Initialisation automatique de window.cryptoTable
  useEffect(() => {
    if (typeof window !== 'undefined' && !window.cryptoTable) {
      const stored = localStorage.getItem('ecotoneai_cryptoTable');
      if (stored) {
        try {
          window.cryptoTable = JSON.parse(stored);
          // Debug log removed: [App] window.cryptoTable initialisée depuis localStorage
        } catch (e) {
          window.cryptoTable = [];
        }
      } else {
        window.cryptoTable = [];
        // Debug log removed: [App] window.cryptoTable initialisée vide
      }
    }
  }, []);

  const handleCurrencyChange = () => setCurrencyRefresh(r => r + 1);
  const handleThemeChange = (value) => {
    document.cookie = `theme=${value};path=/;max-age=31536000`;
    localStorage.setItem('ecotoneai_theme', value);
    document.documentElement.setAttribute('data-theme', value === 'system' ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light') : value);
  };
  useEffect(() => {
    // Au montage, applique le thème depuis localStorage si présent
    const storedTheme = localStorage.getItem('ecotoneai_theme');
    if (storedTheme) {
      document.documentElement.setAttribute('data-theme', storedTheme === 'system' ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light') : storedTheme);
    }
  }, []);

  const location = useLocation();
  const showComingSoonRoot = publicConfig.comingSoon && location.pathname === '/';

  const routes = (
    <Routes>
      <Route path="/" element={publicConfig.comingSoon ? <ComingSoonPage /> : <HomePage />} />
      <Route path="/cryptos" element={<CryptoList key={currencyRefresh} />} />
      <Route path="/cryptos/heatmap" element={<CryptoHeatmapWrapper />} />
      <Route path="/cryptos/:id" element={<CryptoDetail key={currencyRefresh} />} />
      <Route path="/fear-and-greed" element={<FearAndGreed />} />
      <Route path="/btc-dominance" element={<BtcDominanceDetail />} />
      <Route path="/currencies" element={<CurrencyList />} />
      <Route path="/currencies/:id" element={<CurrencyDetail />} />
      <Route path="/unified-portfolio" element={<RequireLoginRoute user={user} authResolved={authResolved}><UnifiedPortfolioPage /></RequireLoginRoute>} />
      <Route path="/dashboard" element={<RequireLoginRoute user={user} authResolved={authResolved}><DashboardUltraPremium /></RequireLoginRoute>} />
      <Route path="/login" element={<LoginPage user={authResolved ? user || null : null} setUser={setUser} />} />
      <Route path="/admin/*" element={<RequireLoginRoute user={user} authResolved={authResolved}><AdminPage /></RequireLoginRoute>} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/about" element={<About />} />
      <Route path="/macro-dominance" element={<MacroDominanceScreen />} />
      <Route path="/market" element={<MarketIntelligencePage />} />
      <Route path="/asset-scanner" element={<AssetScannerPage />} />
      <Route path="/scanner" element={<PatternScannerOverviewPage user={authResolved ? user || null : null} authResolved={authResolved} />} />
      <Route path="/scanner/:symbol" element={<PatternScannerDetailPage user={authResolved ? user || null : null} authResolved={authResolved} />} />
      <Route path="/pattern-scanner" element={<Navigate to="/scanner" replace />} />
      {/* ...existing code... */}
      <Route path="/connections" element={<RequireLoginRoute user={user} authResolved={authResolved}><ConnectionsPage /></RequireLoginRoute>} />
    </Routes>
  );

  if (showComingSoonRoot) {
    return routes;
  }

  return (
    <Layout headerProps={{ onCurrencyChange: handleCurrencyChange, user: authResolved ? user || null : null, setUser, onThemeChange: handleThemeChange }}>
      {/* Debug log removed: [App] Avant <Routes> rendu */}
      {routes}
    </Layout>
  );
}

function App() {
  return (
    <Router>
      <AppRoutes />
    </Router>
  );
}

export default App;
